import { useReducer, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import {
  ConfirmStep,
  type SubmitMode,
} from '@/components/campaign-import/confirm-step';
import { FileStep } from '@/components/campaign-import/file-step';
import { ImportWizardPresenter } from '@/components/campaign-import/import-wizard.presenter';
import {
  createInitialWizardState,
  getRowsToSend,
  importWizardReducer,
  type SubmitFailureReason,
} from '@/components/campaign-import/import-wizard.reducer';
import { MappingStep } from '@/components/campaign-import/mapping-step';
import { ResultView } from '@/components/campaign-import/result-view';
import { ReviewStep } from '@/components/campaign-import/review-step';
import {
  campaignImportBatchesControllerConfirm,
  campaignImportBatchesControllerCreate,
} from '@/generated/korza-api-client';
import type { CreateCampaignImportBatchResponseDto } from '@/generated/korza-api-client/types.gen';
import { useTenant } from '@/hooks/use-tenant';
import { SidebarLayout } from '@/layouts/sidebar.layout';

class ImportSubmitError extends Error {
  constructor(public readonly reason: SubmitFailureReason) {
    super(reason);
    this.name = 'ImportSubmitError';
  }
}

async function createBatch(
  tenantSlug: string,
  fileName: string,
  rows: Parameters<
    typeof campaignImportBatchesControllerCreate
  >[0]['body']['rows']
): Promise<CreateCampaignImportBatchResponseDto> {
  let response;
  try {
    response = await campaignImportBatchesControllerCreate({
      path: { tenantSlug },
      body: { fileName, rows },
    });
  } catch {
    throw new ImportSubmitError('network');
  }
  if (response.response.status === 409) throw new ImportSubmitError('conflict');
  if (response.error || !response.data) throw new ImportSubmitError('server');
  return response.data;
}

/** Confirms the whole batch: `campaignIds` is omitted, never an empty list. */
async function confirmBatch(tenantSlug: string, batchId: string) {
  const response = await campaignImportBatchesControllerConfirm({
    path: { tenantSlug, batchId },
    body: {},
    throwOnError: true,
  });
  return response.data;
}

export default function CampaignImportPage() {
  const tenant = useTenant();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const storeWebsiteDomain = tenant.storeWebsiteDomain?.trim() ?? '';
  const [state, dispatch] = useReducer(
    importWizardReducer,
    undefined,
    createInitialWizardState
  );

  const invalidateCampaigns = () =>
    queryClient.invalidateQueries({ queryKey: ['campaigns', tenant.slug] });

  // Scheduling is its own request: when it fails the batch already exists, so the retry
  // confirms again and never creates again.
  const scheduleInFlight = useRef(false);
  const scheduleMutation = useMutation({
    mutationFn: (batchId: string) => confirmBatch(tenant.slug, batchId),
    onMutate: () => dispatch({ type: 'scheduleStarted' }),
    onSettled: () => {
      scheduleInFlight.current = false;
    },
    onSuccess: async (result, batchId) => {
      dispatch({ type: 'scheduleSucceeded', result });
      await Promise.all([
        invalidateCampaigns(),
        queryClient.invalidateQueries({
          queryKey: ['campaign-import-batch', tenant.slug, batchId],
        }),
      ]);
    },
    onError: () => dispatch({ type: 'scheduleFailed' }),
  });

  const scheduleBatch = (batchId: string) => {
    if (scheduleInFlight.current) return;
    scheduleInFlight.current = true;
    scheduleMutation.mutate(batchId);
  };

  const inFlight = useRef(false);
  const submitMutation = useMutation({
    mutationFn: (_mode: SubmitMode) =>
      createBatch(
        tenant.slug,
        state.file?.fileName ?? '',
        getRowsToSend(state)
      ),
    onMutate: () => dispatch({ type: 'submitStarted' }),
    onSettled: () => {
      inFlight.current = false;
    },
    onSuccess: (result, mode) => {
      dispatch({ type: 'submitSucceeded', result });
      void invalidateCampaigns();
      if (mode === 'schedule' && result.batch && result.createdCount > 0) {
        scheduleBatch(result.batch.id);
      }
    },
    onError: (error) =>
      dispatch({
        type: 'submitFailed',
        reason: error instanceof ImportSubmitError ? error.reason : 'server',
      }),
  });

  // One create at a time. The buttons are disabled while sending; the ref also covers clicks
  // that arrive before that state renders.
  const handleSubmit = (mode: SubmitMode) => {
    if (inFlight.current) return;
    inFlight.current = true;
    submitMutation.mutate(mode);
  };

  const doneResult =
    state.submission.status === 'done' ? state.submission.result : null;

  return (
    <SidebarLayout>
      <ImportWizardPresenter
        step={state.step}
        websiteDomainConfigured={storeWebsiteDomain !== ''}
      >
        {state.step === 'file' ? (
          <FileStep
            storeWebsiteDomain={storeWebsiteDomain}
            onFileLoaded={(loaded) =>
              dispatch({
                type: 'fileLoaded',
                ...loaded,
                storeWebsiteDomain,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
              })
            }
          />
        ) : null}
        {state.step === 'mapping' ? (
          <MappingStep state={state} dispatch={dispatch} />
        ) : null}
        {state.step === 'review' ? (
          <ReviewStep state={state} dispatch={dispatch} />
        ) : null}
        {state.step === 'confirm' ? (
          <ConfirmStep
            state={state}
            dispatch={dispatch}
            onSubmit={handleSubmit}
          />
        ) : null}
        {state.step === 'result' && doneResult ? (
          <ResultView
            result={doneResult}
            file={state.file}
            storeWebsiteDomain={storeWebsiteDomain}
            schedule={state.schedule}
            onSchedule={() => {
              if (doneResult.batch) scheduleBatch(doneResult.batch.id);
            }}
            onViewBatch={(batchId) =>
              navigate(`/${tenant.slug}/campaigns/import/${batchId}`)
            }
            onImportAnother={() => dispatch({ type: 'reset' })}
          />
        ) : null}
      </ImportWizardPresenter>
    </SidebarLayout>
  );
}
